import { ReactNode, useMemo, useEffect, useState, lazy, Suspense } from 'react';
import { getRuntimeConfig } from 'src/utils/runtimeConfig';
import { isAuthDisabled, getAuthProvider } from 'src/auth/authConfig';
import { useAuth } from 'src/auth/useAuth';
import { useTenantInitialization } from 'src/hooks/useTenantInitialization';
import { useTenancyMode } from 'src/hooks/useTenancyMode';
import { getAuthFeatureFlags, isUserInRollout } from 'src/utils/featureFlags';
import { AuthErrorBoundary } from 'src/components/auth/AuthErrorBoundary';
import { LoadingState, AuthLoadingState } from 'src/components/LoadingState';
import AuthProtectedRoute from 'src/utils/AuthProtectedRoute';
import EntraAuthProvider from 'src/auth/EntraAuthProvider';
import { useMagicLink } from 'src/stores/magicLink';

// Lazy load Layout to reduce main bundle size
const Layout = lazy(() => import('src/layout/index'));
const UnauthorizedAccess = lazy(() => import('src/pages/UnauthorizedAccess'));

/**
 * Authentication State Types
 */
type AuthenticationPhase =
  | 'initializing'
  | 'authenticating'
  | 'tenant-setup'
  | 'unauthorized'
  | 'ready'
  | 'error';

interface AuthenticationState {
  phase: AuthenticationPhase;
  message: string;
  authProvider?: string;
  error?: Error;
}

type EffectiveProvider = 'keycloak' | 'entra' | 'magic-link';
type FeatureFlags = ReturnType<typeof getAuthFeatureFlags>;

/**
 * Helper function to determine authentication readiness
 */
function getAuthenticationState() {
  const authProvider = getAuthProvider();
  const authDisabled = isAuthDisabled();
  // Entra requires waiting for auth, magic-link and keycloak don't
  const shouldWaitForAuth = !authDisabled && authProvider === 'entra';

  return { authProvider, authDisabled, shouldWaitForAuth };
}

/**
 * Helper function to check if environment tenant bypass should be used
 */
function shouldUseEnvironmentBypass(
  isInitialized: boolean,
  isLoading: boolean,
  isSingleTenantMode: boolean,
): boolean {
  const config = getRuntimeConfig();
  const hasEnvTenant = Boolean(
    config.VITE_TENANT_ID && config.VITE_TENANT_ID !== 'replace-this',
  );

  return hasEnvTenant && !isInitialized && !isLoading && !isSingleTenantMode;
}

/**
 * Authentication Content Component
 * Renders the appropriate authentication provider
 */
interface AuthContentProps {
  readonly effectiveProvider: EffectiveProvider;
  readonly children: ReactNode;
}

function AuthContent({ effectiveProvider, children }: AuthContentProps) {
  if (effectiveProvider === 'entra') {
    return <EntraAuthProvider>{children}</EntraAuthProvider>;
  }

  // Magic link and Keycloak both use AuthProtectedRoute
  // Magic link authentication is automatically detected when tokens are present
  return <AuthProtectedRoute>{children}</AuthProtectedRoute>;
}

/**
 * Helper function to determine authentication state for Entra ID
 */
function getEntraAuthState(
  authenticated: boolean,
  isInitialized: boolean,
  isLoading: boolean,
  effectiveProvider: string,
): AuthenticationState {
  if (!authenticated) {
    return {
      phase: 'authenticating',
      message: `Authenticating with ${effectiveProvider}...`,
      authProvider: effectiveProvider,
    };
  }

  if (!isInitialized || isLoading) {
    return {
      phase: 'tenant-setup',
      message: 'Setting up tenant configuration...',
      authProvider: effectiveProvider,
    };
  }

  return {
    phase: 'ready',
    message: 'Application ready',
    authProvider: effectiveProvider,
  };
}

/**
 * Helper function to determine authentication state for Keycloak and Magic Link
 */
function getKeycloakAuthState(
  authInitialized: boolean,
  authenticated: boolean,
  isInitialized: boolean,
  isLoading: boolean,
  effectiveProvider: string,
): AuthenticationState {
  if (!authInitialized) {
    return {
      phase: 'initializing',
      message: `Initializing ${effectiveProvider} authentication...`,
      authProvider: effectiveProvider,
    };
  }

  if (!authenticated) {
    return {
      phase: 'authenticating',
      message: `Authenticating with ${effectiveProvider}...`,
      authProvider: effectiveProvider,
    };
  }

  if (!isInitialized || isLoading) {
    return {
      phase: 'tenant-setup',
      message: 'Setting up tenant configuration...',
      authProvider: effectiveProvider,
    };
  }

  return {
    phase: 'ready',
    message: 'Application ready',
    authProvider: effectiveProvider,
  };
}

/**
 * Input bag for computeNextAuthState.
 * Grouping into an object keeps the parameter count within SonarQube's limit.
 */
interface AuthStateInput {
  authDisabled: boolean;
  authorizationError: boolean;
  effectiveProvider: EffectiveProvider;
  isMagicLinkAuthenticated: string | false | undefined;
  isInitialized: boolean;
  isLoading: boolean;
  authInitialized: boolean;
  authenticated: boolean;
}

/**
 * Pure function that derives the next AuthenticationState from all relevant
 * inputs. Extracted from useEffect to remove all branching from the component
 * body. Returns a new AuthenticationState on every invocation.
 */
function computeNextAuthState({
  authDisabled,
  authorizationError,
  effectiveProvider,
  isMagicLinkAuthenticated,
  isInitialized,
  isLoading,
  authInitialized,
  authenticated,
}: AuthStateInput): AuthenticationState {
  if (authDisabled) {
    return { phase: 'ready', message: 'Authentication disabled' };
  }

  if (authorizationError) {
    return {
      phase: 'unauthorized',
      message: 'Unauthorized access',
      authProvider: effectiveProvider,
    };
  }

  // For magic-link, authentication is already complete (tokens are present).
  // Skip directly to tenant-setup or ready phase.
  if (effectiveProvider === 'magic-link' && isMagicLinkAuthenticated) {
    if (!isInitialized || isLoading) {
      return {
        phase: 'tenant-setup',
        message: 'Setting up tenant configuration...',
        authProvider: effectiveProvider,
      };
    }
    return {
      phase: 'ready',
      message: 'Application ready',
      authProvider: effectiveProvider,
    };
  }

  // Use provider-specific helpers for Entra ID and Keycloak
  if (effectiveProvider === 'entra') {
    return getEntraAuthState(
      authenticated,
      isInitialized,
      isLoading,
      effectiveProvider,
    );
  }

  return getKeycloakAuthState(
    authInitialized,
    authenticated,
    isInitialized,
    isLoading,
    effectiveProvider,
  );
}

/**
 * Helper function to render content with optional error boundary
 */
function renderWithErrorBoundary(
  content: React.ReactElement,
  featureFlags: FeatureFlags,
): React.ReactElement {
  if (featureFlags.enableEnhancedErrorHandling) {
    return (
      <AuthErrorBoundary
        onError={(error, errorInfo) => {
          console.error('Authentication Error:', error, errorInfo);
        }}
        maxRetries={3}
      >
        {content}
      </AuthErrorBoundary>
    );
  }

  return content;
}

/**
 * Shared lazy-loaded Layout wrapped in Suspense.
 * Extracted to avoid repeating the same JSX in multiple early-return branches.
 */
function renderLayout(): React.ReactElement {
  return (
    <Suspense fallback={<LoadingState message="Loading application..." />}>
      <Layout />
    </Suspense>
  );
}

/**
 * Renders the UI for non-ready authentication phases.
 * Covers: error (re-throw), unauthorized, entra loading, magic-link pending,
 * and the generic provider loading fallback.
 */
function renderPendingAuthContent(
  authState: AuthenticationState,
  effectiveProvider: EffectiveProvider,
  isMagicLinkAuthenticated: string | false | undefined,
  featureFlags: FeatureFlags,
  authInitialized: boolean,
): React.ReactElement | null {
  if (authState.phase === 'error' && authState.error) {
    throw authState.error; // Let error boundary handle it
  }

  if (authState.phase === 'unauthorized') {
    return (
      <Suspense fallback={<LoadingState message="Loading application..." />}>
        <UnauthorizedAccess />
      </Suspense>
    );
  }

  // For Entra ID, always render the provider so it can handle authentication
  if (effectiveProvider === 'entra') {
    return renderWithErrorBoundary(
      <AuthContent effectiveProvider={effectiveProvider}>
        {renderLoadingState(authState, featureFlags, authInitialized)}
      </AuthContent>,
      featureFlags,
    );
  }

  // For magic-link, if authenticated, render immediately (tokens are present)
  if (effectiveProvider === 'magic-link' && isMagicLinkAuthenticated) {
    return renderWithErrorBoundary(
      <AuthContent effectiveProvider={effectiveProvider}>
        {renderLayout()}
      </AuthContent>,
      featureFlags,
    );
  }

  // For other providers, show loading state
  return renderLoadingState(authState, featureFlags, authInitialized);
}

/**
 * Renders fully authenticated content wrapped in the correct auth provider
 * and an optional error boundary.
 */
function renderAuthenticatedContent(
  effectiveProvider: EffectiveProvider,
  featureFlags: FeatureFlags,
): React.ReactElement {
  return renderWithErrorBoundary(
    <AuthContent effectiveProvider={effectiveProvider}>
      {renderLayout()}
    </AuthContent>,
    featureFlags,
  );
}

/**
 * Helper function to render loading states
 */
function renderLoadingState(
  authState: AuthenticationState,
  featureFlags: FeatureFlags,
  authInitialized: boolean,
): React.ReactElement {
  // Use enhanced loading state if available
  if (featureFlags.enableEnhancedLoadingStates && authState.authProvider) {
    return (
      <AuthLoadingState
        provider={authState.authProvider}
        message={authState.message}
      />
    );
  }

  // Fallback to basic loading state
  return (
    <LoadingState
      message={authState.message}
      showAuthInfo={import.meta.env.DEV}
      authInfo={{
        authProvider: authState.authProvider ?? 'unknown',
        authInitialized,
      }}
    />
  );
}

/**
 * Unified AuthenticatedApp Component
 *
 * This component consolidates the complex authentication wrapper chain
 * (AuthProvider → TenantInitializer → Layout) into a single, efficient
 * boundary component that:
 *
 * 1. Manages authentication state and provider selection
 * 2. Handles tenant initialization sequencing
 * 3. Provides unified loading states and error handling
 * 4. Maintains backward compatibility with dual auth system
 * 5. Reduces re-render cycles and initialization complexity
 *
 * Features:
 * - Dual authentication support (Keycloak + Microsoft Entra ID)
 * - Feature flag-based provider selection with rollout support
 * - Authentication bypass mechanisms (VITE_DISABLE_AUTH)
 * - Tenant initialization waiting for auth completion
 * - Enhanced error boundary integration
 * - Consolidated loading states for better UX
 * - Session state management preservation
 */
export function AuthenticatedApp() {
  const [authState, setAuthState] = useState<AuthenticationState>({
    phase: 'initializing',
    message: 'Initializing application...',
  });

  // Get authentication configuration
  const authProvider = useMemo(() => {
    try {
      return getAuthProvider();
    } catch (error) {
      setAuthState({
        phase: 'error',
        message: 'Authentication configuration error',
        error: error as Error,
      });
      return 'keycloak'; // fallback
    }
  }, []);

  // Check if authentication is disabled
  const authDisabled = useMemo(() => isAuthDisabled(), []);

  // Get feature flags and rollout status
  const featureFlags = useMemo(() => getAuthFeatureFlags(), []);
  const isInRollout = useMemo(() => isUserInRollout('auth-provider'), []);

  // Get authentication hooks (always called in same order)
  const { initialized: authInitialized, authenticated } = useAuth();
  const magicLinkState = useMagicLink();

  // Get tenant-related state
  const { isSingleTenantMode } = useTenancyMode();
  const { shouldWaitForAuth } = getAuthenticationState();

  // Check if user is authenticated via magic link (regardless of AUTH_PROVIDER)
  const isMagicLinkAuthenticated =
    magicLinkState.authenticated && magicLinkState.accessToken;

  // Determine effective provider (with rollout logic) - moved up to use in tenant init
  // Priority: 1. Magic link (if authenticated), 2. Configured provider
  const effectiveProvider = useMemo((): EffectiveProvider => {
    if (authDisabled) return 'keycloak'; // doesn't matter when disabled

    // If authenticated via magic link, use magic-link provider
    if (isMagicLinkAuthenticated) {
      return 'magic-link';
    }

    return isInRollout ? authProvider : 'keycloak';
  }, [authDisabled, isInRollout, authProvider, isMagicLinkAuthenticated]);

  // Determine if authentication is ready for tenant initialization
  const authReady = !shouldWaitForAuth || (authInitialized && authenticated);

  // Initialize tenant system after auth is ready
  const { isInitialized, isLoading, authorizationError } =
    useTenantInitialization(authReady);

  // Update authentication state based on current phase
  useEffect(() => {
    setAuthState(
      computeNextAuthState({
        authDisabled,
        authorizationError,
        effectiveProvider,
        isMagicLinkAuthenticated,
        isInitialized,
        isLoading,
        authInitialized,
        authenticated,
      }),
    );
  }, [
    authDisabled,
    authInitialized,
    authenticated,
    shouldWaitForAuth,
    authReady,
    isInitialized,
    isLoading,
    authorizationError,
    effectiveProvider,
    isMagicLinkAuthenticated,
  ]);

  // Handle authentication bypass for disabled auth
  if (authDisabled) {
    return renderLayout();
  }

  // Handle environment tenant bypass
  if (
    authState.phase === 'ready' &&
    shouldUseEnvironmentBypass(isInitialized, isLoading, isSingleTenantMode)
  ) {
    return renderLayout();
  }

  // Show loading/pending states for non-ready phases
  if (authState.phase !== 'ready') {
    return renderPendingAuthContent(
      authState,
      effectiveProvider,
      isMagicLinkAuthenticated,
      featureFlags,
      authInitialized,
    );
  }

  // Render authenticated content with appropriate provider
  return renderAuthenticatedContent(effectiveProvider, featureFlags);
}

export default AuthenticatedApp;
